import { get } from 'lodash';
import moment from 'moment';

/**
 * Check if loan is interest-only or no-payment type.
 * @param {Object} loanTerm - Loan setup data from /setup endpoint
 * @returns {boolean}
 */
export const isInterestOnlyLoan = (loanTerm) => {
  const code = get(loanTerm, 'data.repaymentType.code', '');
  return ['interest-only', 'no-payment'].includes(code);
};

/**
 * Compute the maturity warning variant from loan data.
 * Uses finalPaymentDate from the status API when it falls within 30 days.
 * @param {Object} params
 * @param {Object} params.loanTerm - Loan setup data from /setup endpoint
 * @param {Object} params.loanDetail - Loan status data from /status endpoint
 * @returns {{ variant: 'nearMaturity'|'matured'|null, fullAmountDueDate: string|null, graceDeadlineDate: string|null }}
 */
export const resolveMaturityWarningState = ({ loanTerm, loanDetail }) => {
  const nullResult = { variant: null, fullAmountDueDate: null, graceDeadlineDate: null };

  // Guard: check loan type
  const repaymentCode = get(loanTerm, 'data.repaymentType.code', '');
  const isNoPaymentType = ['interest-only', 'no-payment'].includes(repaymentCode);

  if (!isNoPaymentType) {
    return nullResult;
  }

  // Use finalPaymentDate from status API as primary source
  const finalPaymentDateRaw = get(loanDetail, 'data.0.finalPaymentDate');
  const maturityDateRaw = get(loanTerm, 'data.maturityDate');

  // Determine the effective due date — prefer finalPaymentDate from status API
  const effectiveDateRaw = finalPaymentDateRaw || maturityDateRaw;
  if (!effectiveDateRaw) {
    return nullResult;
  }

  const effectiveDate = moment(effectiveDateRaw);
  if (!effectiveDate.isValid()) {
    return nullResult;
  }

  const today = moment().startOf('day');
  const daysUntilDue = effectiveDate.diff(today, 'days');
  const daysPastDue = Number(get(loanDetail, 'data.0.daysPastDue', 0)) || 0;

  // Matured + in grace period (DPD 1–17): red alert, highest priority
  if (daysUntilDue <= 0 && daysPastDue >= 1 && daysPastDue <= 17) {
    const gracePeriodDays = 17;
    const graceDeadline = effectiveDate.clone().add(gracePeriodDays, 'days');

    return {
      variant: 'maturedGrace',
      fullAmountDueDate: effectiveDate.format('MM/DD/YYYY'),
      graceDeadlineDate: graceDeadline.format('MM/DD/YYYY'),
    };
  }

  // Near maturity: within 30-day window
  if (daysUntilDue > 0 && daysUntilDue <= 30) {
    return {
      variant: 'nearMaturity',
      fullAmountDueDate: effectiveDate.format('MM/DD/YYYY'),
      graceDeadlineDate: null,
    };
  }

  // Matured: past due date (beyond grace period)
  if (daysUntilDue <= 0) {
    const gracePeriodDays = 17;
    const graceDeadline = effectiveDate.clone().add(gracePeriodDays, 'days');

    return {
      variant: 'matured',
      fullAmountDueDate: effectiveDate.format('MM/DD/YYYY'),
      graceDeadlineDate: graceDeadline.format('MM/DD/YYYY'),
    };
  }

  // More than 30 days from due date — no warning
  return nullResult;
};
